import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  Output,
  Renderer2
} from '@angular/core';
import {pxToMm} from '../helpers/image.helper';

export interface ResizeEndEvent {
  xMm?: number;
  yMm?: number;
  wMm?: number;
  hMm?: number;
}

type Handle =
  | 'n' | 's' | 'e' | 'w'
  | 'ne' | 'nw' | 'se' | 'sw';

@Directive({
  standalone: true,
  selector: '[appResizable]'
})
export class ResizableDirective implements OnDestroy {
  @Input() resizableDisabled = false;
  @Output() resizeEnd = new EventEmitter<ResizeEndEvent>();

  private handles: HTMLElement[] = [];
  private activeHandle: Handle | null = null;

  private startX = 0;
  private startY = 0;
  private startRect!: DOMRect;

  constructor(
    private el: ElementRef<HTMLElement>,
    private r: Renderer2,
    private zone: NgZone
  ) {
    this.createHandles();
  }

  ngOnDestroy(): void {
    for (const h of this.handles) h.remove();
  }

  @HostListener('mouseenter')
  onEnter() {
    if (this.resizableDisabled) return;
    for (const h of this.handles) h.style.display = 'block';
  }

  @HostListener('mouseleave')
  onLeave() {
    if (this.activeHandle) return;
    for (const h of this.handles) h.style.display = 'none';
  }

  private createHandles() {
    const positions: Handle[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];
    for (const p of positions) {
      const h = this.r.createElement('div') as HTMLElement;
      this.r.addClass(h, 'resize-handle');
      this.r.addClass(h, `h-${p}`);
      this.r.setStyle(h, 'position', 'absolute');
      this.r.setStyle(h, 'width', '8px');
      this.r.setStyle(h, 'height', '8px');
      this.r.setStyle(h, 'background', '#1976d2');
      this.r.setStyle(h, 'borderRadius', '2px');
      this.r.setStyle(h, 'zIndex', '9999');
      this.r.setStyle(h, 'display', 'none');
      this.positionHandle(h, p);

      h.addEventListener('mousedown', (e) => this.onHandleDown(e, p));
      this.el.nativeElement.appendChild(h);
      this.handles.push(h);
    }

    // só para garantir que o host permita handles por cima
    const hostPos = getComputedStyle(this.el.nativeElement).position;
    if (hostPos === 'static') this.r.setStyle(this.el.nativeElement, 'position', 'absolute');
  }

  private positionHandle(h: HTMLElement, p: Handle) {
    const s = (prop: string, v: string) => this.r.setStyle(h, prop, v);
    // reset
    ['top', 'left', 'right', 'bottom', 'transform', 'cursor'].forEach(k => s(k, ''));

    const cursors: Record<Handle, string> = {
      n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize',
      ne: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize', sw: 'nesw-resize'
    };
    s('cursor', cursors[p]);

    if (p.includes('n')) s('top', '-4px');
    if (p.includes('s')) s('bottom', '-4px');
    if (p.includes('e')) s('right', '-4px');
    if (p.includes('w')) s('left', '-4px');

    if (p === 'n' || p === 's') {
      s('left', '50%');
      s('transform', 'translateX(-50%)');
    }
    if (p === 'e' || p === 'w') {
      s('top', '50%');
      s('transform', 'translateY(-50%)');
    }
    if (p === 'ne') { /* top+right already */
    }
    if (p === 'nw') { /* top+left already */
    }
    if (p === 'se') { /* bottom+right already */
    }
    if (p === 'sw') { /* bottom+left already */
    }
  }

  private onHandleDown(e: MouseEvent, handle: Handle) {
    if (this.resizableDisabled) return;
    e.preventDefault();
    e.stopPropagation();

    this.activeHandle = handle;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.startRect = this.el.nativeElement.getBoundingClientRect();

    this.zone.runOutsideAngular(() => {
      window.addEventListener('mousemove', this.onMove, {passive: false});
      window.addEventListener('mouseup', this.onUp, {passive: false});
    });
  }

  private onMove = (e: MouseEvent) => {
    if (!this.activeHandle) return;
    e.preventDefault();

    const dx = e.clientX - this.startX;
    const dy = e.clientY - this.startY;

    // aplica prévia em px via style, mas emite final em mm no mouseup
    const host = this.el.nativeElement;

    let newLeftPx = host.offsetLeft;
    let newTopPx = host.offsetTop;
    let newWpx = host.offsetWidth;
    let newHpx = host.offsetHeight;

    const h = this.activeHandle;

    if (h.includes('e')) newWpx = Math.max(1, this.startRect.width + dx);
    if (h.includes('s')) newHpx = Math.max(1, this.startRect.height + dy);
    if (h.includes('w')) {
      newWpx = Math.max(1, this.startRect.width - dx);
      newLeftPx = host.offsetLeft + dx;
    }
    if (h.includes('n')) {
      newHpx = Math.max(1, this.startRect.height - dy);
      newTopPx = host.offsetTop + dy;
    }

    host.style.width = `${newWpx}px`;
    host.style.height = `${newHpx}px`;
    host.style.left = `${newLeftPx}px`;
    host.style.top = `${newTopPx}px`;
  };

  private onUp = (e: MouseEvent) => {
    if (!this.activeHandle) return;
    e.preventDefault();

    const host = this.el.nativeElement;

    // converte px atuais do host para mm e emite
    const xMm = pxToMm(host.offsetLeft);
    const yMm = pxToMm(host.offsetTop);
    const wMm = pxToMm(host.offsetWidth);
    const hMm = pxToMm(host.offsetHeight);

    this.activeHandle = null;
    for (const h of this.handles) h.style.display = 'none';

    window.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('mouseup', this.onUp);

    this.resizeEnd.emit({xMm, yMm, wMm, hMm});
  };
}
